/**
 * Arch Hangar Generator
 *
 * Generates building and unit configurations from arch hangar parameters.
 * Single-unit template: one building = one open space under an arch shell.
 */

import type { GeneratedBuilding, GeneratedUnit, UnitEdge, DoorSpec, DoorWall } from '../types'
import type { ArchHangarParams } from './params'
import { archDoorToDoorSpec } from './params'
import { calculateTotalHeight } from './calculations'

/**
 * Generate building configuration from arch hangar parameters
 */
export function generateBuilding(params: ArchHangarParams): GeneratedBuilding {
  const rotationRadians = (params.bearing_deg * Math.PI) / 180
  const totalHeight = calculateTotalHeight(params.crown_height_ft, params.knee_wall_height_ft)

  // Build flat door array for settings.doors
  const flatDoors: Array<{
    wall: DoorWall
    width_ft: number
    height_ft: number
    offset_ft: number
    type: typeof params.front_door.type
  }> = []

  const frontDoorSpec = archDoorToDoorSpec(params.front_door, params.span_ft)
  if (frontDoorSpec) {
    flatDoors.push({ wall: 'front', ...frontDoorSpec })
  }

  const backDoorSpec = archDoorToDoorSpec(params.back_door, params.span_ft)
  if (backDoorSpec) {
    flatDoors.push({ wall: 'back', ...backDoorSpec })
  }

  // Primary door for legacy fields
  const primaryDoor = frontDoorSpec || backDoorSpec

  return {
    name: params.name,
    code: params.code,
    type: 'arch_hangar',
    exterior_width: params.span_ft,
    exterior_depth: params.length_ft,
    exterior_height: totalHeight,
    latitude: params.latitude,
    longitude: params.longitude,
    rotation: rotationRadians,
    door_width: primaryDoor?.width_ft || 60,
    door_height: primaryDoor?.height_ft || 20,
    settings: {
      floor_color: params.floor_color,
      wall_color: params.wall_color,
      doors: flatDoors,
    },
    type_config: {
      template: 'arch_hangar',
      span_ft: params.span_ft,
      length_ft: params.length_ft,
      crown_height_ft: params.crown_height_ft,
      knee_wall_height_ft: params.knee_wall_height_ft,
      arch_profile: params.arch_profile,
      arch_segments: params.arch_segments,
      wall_thickness_ft: params.wall_thickness_ft,
      front_door: params.front_door,
      back_door: params.back_door,
    },
  }
}

/**
 * Generate units for the arch hangar (single unit)
 */
export function generateUnits(
  building: GeneratedBuilding,
  params: ArchHangarParams
): GeneratedUnit[] {
  const frontDoorSpec = archDoorToDoorSpec(params.front_door, params.span_ft)
  const backDoorSpec = archDoorToDoorSpec(params.back_door, params.span_ft)

  const frontSpecs: DoorSpec[] = frontDoorSpec ? [frontDoorSpec] : []
  const backSpecs: DoorSpec[] = backDoorSpec ? [backDoorSpec] : []

  // Edges for collision detection — arch sides are exterior but rendered by ArchRenderer, not WallRenderer
  // Front/back end walls have doors and are rendered as flat walls by ArchRenderer
  const edges: UnitEdge[] = [
    {
      side: 'front',
      type: 'exterior',
      canBeShared: false,
      openings: frontSpecs.length > 0 ? frontSpecs : undefined,
    },
    {
      side: 'back',
      type: 'exterior',
      canBeShared: false,
      openings: backSpecs.length > 0 ? backSpecs : undefined,
    },
    // Left and right edges exist for data but the arch shell handles their rendering
    {
      side: 'left',
      type: 'exterior',
      canBeShared: false,
    },
    {
      side: 'right',
      type: 'exterior',
      canBeShared: false,
    },
  ]

  const totalHeight = calculateTotalHeight(params.crown_height_ft, params.knee_wall_height_ft)

  return [
    {
      code: params.code,
      type: 'conventional_hangar',
      capacity_strategy: 'stackable',
      interior_width: params.span_ft,
      interior_depth: params.length_ft,
      interior_height: totalHeight,
      door_width: building.door_width,
      door_height: building.door_height,
      door_type: params.front_door.enabled
        ? params.front_door.type
        : params.back_door.enabled
          ? params.back_door.type
          : 'bifold',
      offset_x: 0,
      offset_y: 0,
      unit_rotation: 0,
      edges,
    },
  ]
}
